import { useState, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import { Onboarding } from "./components/Onboarding";
import { AppLayout } from "./components/layout/AppLayout";
import { supabase } from "@/integrations/supabase/client";
import LoadingSplash from "@/components/LoadingSplash";
import Home from "./pages/Home";
import Dashboard from "./pages/Dashboard";
import Compliance from "./pages/Compliance";
import Programs from "./pages/compliance/Programs";
import ProgramDetail from "./pages/compliance/ProgramDetail";
import Evidence from "./pages/compliance/Evidence";
import ComplianceTasks from "./pages/compliance/Tasks";
import TaskDetail from "./pages/compliance/TaskDetail";
import AuditorPage from "./pages/compliance/Auditor";
import Begin from "./pages/Begin";
import PolicyCreator from "./pages/PolicyCreator";
import PolicyLibrary from "./pages/PolicyLibrary";
import PolicyView from "./pages/PolicyView";
import PolicyWorkflowStatus from "./pages/PolicyWorkflowStatus";
import PolicyExpiredRenewals from "./pages/PolicyExpiredRenewals";
import Templates from "./pages/Templates";
import Feedback from "./pages/Feedback";
import Team from "./pages/Team";
import ActivityLog from "./pages/ActivityLog";
import Settings from "./pages/Settings";
import AIAgentPage from "./pages/AIAgentPage";
import OnboardingPage from "./pages/OnboardingPage";
import Billing from "./pages/Billing";
import Auth from "./pages/Auth";
import NotFound from "./pages/NotFound";
import Landing from "./pages/Landing";
import Consulting from "./pages/Consulting";
import PolicyMarketing from "./pages/PolicyMarketing";
import ComplianceFeature from "./pages/features/ComplianceFeature";
import RiskFeature from "./pages/features/RiskFeature";
import GovernanceFeature from "./pages/features/GovernanceFeature";
import Quizzes from "./pages/Quizzes";
import KnowledgeBase from "./pages/KnowledgeBase";
import QuizTake from "./pages/QuizTake";
import QuizEdit from "./pages/QuizEdit";
import OrgChartPage from "./pages/governance/OrgChart";
import GovernanceDashboard from "./pages/governance/GovernanceDashboard";
import TorList from "./pages/governance/TorList";
import TorEditor from "./pages/governance/TorEditor";
import TorPreview from "./pages/governance/TorPreview";
import TorReview from "./pages/governance/TorReview";
import MeetingMinutes from "./pages/governance/MeetingMinutes";
import VotingDecisions from "./pages/governance/VotingDecisions";
import TaskAutomation from "./pages/governance/TaskAutomation";
import UploadPolicy from "./pages/UploadPolicy";
import RiskOverview from "./pages/risk/Overview";
import RiskRegister from "./pages/risk/RiskRegister";
import RiskHeatMap from "./pages/risk/HeatMap";
import RiskAppetite from "./pages/risk/RiskAppetite";
import { ThemeProvider } from "./contexts/ThemeContext";
import { GovernanceProvider } from "./hooks/useGovernance";
import Present from "./pages/Present";
import PresentSales from "./pages/present/Sales";

const queryClient = new QueryClient();

const AppContent = () => {
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isCheckingOnboarding, setIsCheckingOnboarding] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const timeout = setTimeout(() => {
      if (!cancelled) setIsCheckingOnboarding(false);
    }, 8000);

    const checkOnboardingStatus = async () => {
      try {
        const hasSeenOnboarding = localStorage.getItem('hasSeenOnboarding');
        if (!hasSeenOnboarding) {
          const { data, error } = await supabase.auth.getUser();
          if (!error && data?.user) {
            const { data: profile, error: pErr } = await supabase
              .from('profiles')
              .select('created_at')
              .eq('id', data.user.id)
              .single();
            if (!pErr && profile) {
              const profileCreatedAt = new Date(profile.created_at);
              const now = new Date();
              const hoursSinceCreation = (now.getTime() - profileCreatedAt.getTime()) / (1000 * 60 * 60);
              if (hoursSinceCreation < 24) {
                setShowOnboarding(true);
              }
            }
          }
        }
      } catch (e) {
        console.warn('Onboarding check failed, continuing:', e);
      } finally {
        if (!cancelled) setIsCheckingOnboarding(false);
        clearTimeout(timeout);
      }
    };

    checkOnboardingStatus();
    return () => { cancelled = true; clearTimeout(timeout); };
  }, []);

  const handleOnboardingComplete = () => {
    localStorage.setItem('hasSeenOnboarding', 'true');
    setShowOnboarding(false);
  };

  if (isCheckingOnboarding) {
    return (
      <LoadingSplash title="Preparing your workspace…" subtitle="Securely initializing your session" />
    );
  }

  if (showOnboarding) {
    return (
      <ProtectedRoute>
        <Onboarding onComplete={handleOnboardingComplete} />
      </ProtectedRoute>
    );
  }

  return (
    <ProtectedRoute>
      <GovernanceProvider>
        <AppLayout>
          <Routes>
            <Route path="/" element={<Navigate to="/home" replace />} />
            <Route path="/home" element={<Home />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/compliance" element={<Compliance />} />
            <Route path="/compliance/programs" element={<Programs />} />
            <Route path="/compliance/programs/:id" element={<ProgramDetail />} />
            <Route path="/compliance/tasks" element={<ComplianceTasks />} />
            <Route path="/compliance/evidence" element={<Evidence />} />
            <Route path="/compliance/tasks/:id" element={<TaskDetail />} />
            <Route path="/compliance/auditor" element={<AuditorPage />} />
            <Route
              path="/begin"
              element={
                <ProtectedRoute requiredRoles={['creator', 'admin']}>
                  <Begin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/policy-creator"
              element={
                <ProtectedRoute requiredRoles={['creator', 'admin']}>
                  <PolicyCreator />
                </ProtectedRoute>
              }
            />
            <Route path="/policy-library" element={<PolicyLibrary />} />
            <Route path="/policy/:id" element={<PolicyView />} />
            <Route
              path="/upload-policy"
              element={
                <ProtectedRoute requiredRoles={['creator', 'admin']}>
                  <UploadPolicy />
                </ProtectedRoute>
              }
            />
            <Route path="/workflow/expired-renewals" element={<PolicyExpiredRenewals />} />
            <Route
              path="/billing"
              element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <Billing />
                </ProtectedRoute>
              }
            />
            <Route path="/workflow/:status" element={<PolicyWorkflowStatus />} />
            <Route
              path="/templates"
              element={
                <ProtectedRoute requiredRoles={['creator', 'admin']}>
                  <Templates />
                </ProtectedRoute>
              }
            />
            <Route
              path="/team"
              element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <Team />
                </ProtectedRoute>
              }
            />
            <Route
              path="/governance/dashboard"
              element={
                <ProtectedRoute>
                  <GovernanceDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/governance/tor"
              element={
                <ProtectedRoute>
                  <TorList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/governance/tor/:committeeId"
              element={
                <ProtectedRoute>
                  <TorEditor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/governance/tor/:committeeId/preview"
              element={
                <ProtectedRoute>
                  <TorPreview />
                </ProtectedRoute>
              }
            />
            <Route
              path="/governance/tor/:committeeId/review"
              element={
                <ProtectedRoute>
                  <TorReview />
                </ProtectedRoute>
              }
            />
            <Route
              path="/governance/meetings"
              element={
                <ProtectedRoute>
                  <MeetingMinutes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/governance/voting"
              element={
                <ProtectedRoute>
                  <VotingDecisions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/governance/automation"
              element={
                <ProtectedRoute>
                  <TaskAutomation />
                </ProtectedRoute>
              }
            />
            <Route
              path="/governance/org-chart"
              element={
                <ProtectedRoute>
                  <OrgChartPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/activity-log"
              element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <ActivityLog />
                </ProtectedRoute>
              }
            />
            <Route path="/settings" element={<Settings />} />
            <Route path="/feedback" element={<Feedback />} />
            <Route path="/onboarding" element={<OnboardingPage />} />
            <Route path="/ai-agent" element={<AIAgentPage />} />

            {/* Risk Module Routes */}
            <Route path="/risk" element={<RiskOverview />} />
            <Route path="/risk/overview" element={<RiskOverview />} />
            <Route path="/risk/register" element={<RiskRegister />} />
            <Route path="/risk/heat-map" element={<RiskHeatMap />} />
            <Route path="/risk/appetite" element={<RiskAppetite />} />

            {/* Knowledge Base */}
            <Route path="/knowledge-base" element={<KnowledgeBase />} />

            {/* Quizzes */}
            <Route path="/quizzes" element={<Quizzes />} />
            <Route path="/quiz/:id" element={<QuizTake />} />
            <Route path="/quiz/:id/edit" element={<QuizEdit />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </AppLayout>
      </GovernanceProvider>
    </ProtectedRoute>
  );
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <ThemeProvider>
          <BrowserRouter>
            <AuthProvider>
              <Routes>
                <Route path="/auth" element={<Auth />} />
                <Route path="/welcome" element={<Landing />} />
                <Route path="/consulting" element={<Consulting />} />
                <Route path="/present" element={<Present />} />
                <Route path="/present/sales" element={<PresentSales />} />
                <Route path="/features/policy" element={<PolicyMarketing />} />
                <Route path="/features/compliance" element={<ComplianceFeature />} />
                <Route path="/features/risk" element={<RiskFeature />} />
                <Route path="/features/governance" element={<GovernanceFeature />} />
                <Route path="/*" element={<AppContent />} />
              </Routes>
            </AuthProvider>
          </BrowserRouter>
        </ThemeProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
